import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HeartHandshake } from "lucide-react";
import { interestOptions } from "@/lib/interests";
import { countryList } from "@/lib/countries";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { NearestCity } from "@/components/NearestCity";

// Lot 580 : un compte issu de l'inscription allégée est accompagné par la carte « Complète ton profil » (étape par
// étape, interactions bloquées côté serveur) — ce dialogue bloquant ne le concerne plus. Comptes classiques : inchangé.
const isIncomplete = (u) =>
  !!u && u.signup_mode !== "light" && (!u.gender || !(u.city || "").trim() || !(u.country || "").trim() || !u.denomination || (u.interests || []).length < 3);

// Blocking dialog: users must complete the "common points" info before continuing.
export const CompleteProfileModal = () => {
  const { user, config, lang, refreshMe, t } = useApp();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = isIncomplete(user);

  useEffect(() => {
    if (open && user) {
      setForm((f) => f || {
        gender: user.gender || "",
        city: user.city || "",
        country: (user.country || "").toUpperCase(),
        denomination: user.denomination || "",
        interests: user.interests || [],
      });
    }
  }, [open, user]);

  if (!open || !form) return null;
  const denoms = (config?.denominations || []).map((d) => d.name);
  const countries = countryList(lang);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleInterest = (i) => set("interests", form.interests.includes(i) ? form.interests.filter((x) => x !== i) : [...form.interests, i]);

  const save = async () => {
    if (!form.gender || !form.city.trim() || !form.country || !form.denomination || form.interests.length < 3) {
      toast.error(t("cp.error", "Merci de tout compléter (au moins 3 centres d'intérêt)"));
      return;
    }
    setBusy(true);
    try {
      await api.patch("/users/me", { ...form, country: (form.country || "").toUpperCase() });
      toast.success(t("cp.success", "Merci ! Profil complété 🙏"));
      await refreshMe();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        aria-describedby={undefined}
        variant="sheet"
        className="sm:max-w-md [&_[data-dialog-close]]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="complete-profile-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <HeartHandshake size={20} style={{ color: "var(--yalola-primary)" }} /> {t("cp.title", "Complétez votre profil")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          {t("cp.desc", "Ces informations permettent de vous proposer des « Points en commun » avec d'autres membres. Elles sont obligatoires pour continuer.")}
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("cp.gender", "Sexe")} *</Label>
              <Select value={form.gender || undefined} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="cp-gender"><SelectValue placeholder={t("cp.choose", "Choisir...")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t("cp.male", "Homme")}</SelectItem>
                  <SelectItem value="female">{t("cp.female", "Femme")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("cp.country", "Pays")} *</Label>
              <Select value={form.country || undefined} onValueChange={(v) => set("country", v)}>
                <SelectTrigger data-testid="cp-country"><SelectValue placeholder={t("cp.choose", "Choisir...")} /></SelectTrigger>
                <SelectContent className="max-h-64">{countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("cp.city", "Ville")} *</Label>
            <CityAutocomplete value={form.city} country={form.country} onChange={(v) => set("city", v)} testId="cp-city" />
            {/* Lot 659 : pays + ville en un tap depuis la position du téléphone */}
            <NearestCity className="mt-1.5" onPick={(p) => setForm((f) => ({ ...f, country: p.country, city: p.city }))} testId="cp-nearest" />
          </div>
          <div>
            <Label>{t("cp.denomination", "Obédience / Dénomination")} *</Label>
            <Select value={form.denomination || undefined} onValueChange={(v) => set("denomination", v)}>
              <SelectTrigger data-testid="cp-denomination"><SelectValue placeholder={t("cp.choose", "Choisir...")} /></SelectTrigger>
              <SelectContent className="max-h-64">
                {denoms.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("cp.interests", "Centres d'intérêt (min. 3)")} *</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="cp-interests">
              {interestOptions(config, lang).map(({ key: i, label }) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleInterest(i)}
                  className={`chip ${form.interests.includes(i) ? "!bg-[color:var(--yalola-primary)] !text-white !border-transparent" : ""}`}
                  data-testid={`cp-interest-${i}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        </DialogBody>
        <DialogFooter>
          <Button className="pill-primary rounded-full w-full" onClick={save} disabled={busy} data-testid="cp-save">
            {busy ? t("cp.saving", "Enregistrement...") : t("cp.save", "Valider et continuer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
